import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { LogoLockup } from '../components/LogoLockup';
import { useAuthStore } from '../store/authStore';
import { extractApiError } from '../api/client';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get('email') ?? '';
  const modeParam = searchParams.get('mode');
  const planParam = searchParams.get('plan');
  const [tab, setTab] = useState<'login' | 'register'>(modeParam === 'signup' ? 'register' : 'login');
  const [form, setForm] = useState({ email: emailParam, password: '', name: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');

  const { login, register, googleLogin, isAuthenticated, firstPipelineId } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const googleBtnRef = useRef<HTMLDivElement>(null);

  // Post-login redirect: go back to where the user came from
  const redirectTo = (location.state as { from?: string })?.from;

  useEffect(() => { document.title = 'Sign In — StartupScore'; }, []);

  useEffect(() => {
    if (isAuthenticated) navigate(redirectTo || '/startups', { replace: true });
  }, [isAuthenticated, navigate, redirectTo]);

  // Google Sign-In callback
  const handleGoogleResponse = useCallback(async (response: { credential?: string }) => {
    if (!response.credential) return;
    setIsSubmitting(true);
    setServerError('');
    try {
      const savedRedirect = sessionStorage.getItem('loginRedirect');
      sessionStorage.removeItem('loginRedirect');
      const { isNewUser } = await googleLogin(response.credential);
      const { firstPipelineId: fpId } = useAuthStore.getState();
      if (isNewUser) {
        navigate('/welcome', { replace: true });
      } else {
        navigate(savedRedirect || redirectTo || (fpId ? `/pipeline/${fpId}` : '/startups'), { replace: true });
      }
    } catch (err) {
      const apiErr = extractApiError(err);
      setServerError(apiErr.message || 'Google sign-in failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [googleLogin, navigate, redirectTo]);

  // Load Google Identity Services
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if ((window as any).google?.accounts?.id) {
        (window as any).google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
        });
        if (googleBtnRef.current) {
          (window as any).google.accounts.id.renderButton(googleBtnRef.current, {
            type: 'standard',
            theme: 'filled_black',
            size: 'large',
            width: googleBtnRef.current.offsetWidth,
            text: 'continue_with',
            shape: 'rectangular',
            logo_alignment: 'left',
          });
        }
      }
    };
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [handleGoogleResponse]);

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.email) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Invalid email address';
    if (!form.password) errs.password = 'Password is required';
    else if (form.password.length < 8) errs.password = 'Password must be at least 8 characters';
    if (tab === 'register') {
      if (!form.name.trim()) errs.name = 'Name is required';
      else if (form.name.trim().length < 3) errs.name = 'Name must be at least 3 characters';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError('');
    if (!validate()) {
      document.querySelector('.error-text')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);
    try {
      // FIX: #4b — read saved redirect from sessionStorage
      const savedRedirect = sessionStorage.getItem('loginRedirect');
      sessionStorage.removeItem('loginRedirect');
      if (tab === 'login') {
        await login(form.email, form.password);
        const { firstPipelineId: fpId } = useAuthStore.getState();
        navigate(savedRedirect || redirectTo || (fpId ? `/pipeline/${fpId}` : '/startups'), { replace: true });
      } else {
        await register(form.email, form.password, form.name);
        navigate('/welcome', { replace: true });
      }
    } catch (err) {
      const apiErr = extractApiError(err);
      if (apiErr.code === 'EMAIL_IN_USE') {
        setServerError('An account with this email already exists. Try signing in instead.');
      } else if (apiErr.fields) {
        setErrors(apiErr.fields);
      } else {
        setServerError(apiErr.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#050510', position: 'relative', overflow: 'hidden', padding: '1rem' }}>
      {/* Ambient background */}
      <div aria-hidden style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(255,255,255,0.45) 1px, transparent 1px)', backgroundSize: '32px 32px', opacity: 0.07 }} />
        <div style={{ position: 'absolute', width: 700, height: 700, borderRadius: '50%', top: -280, left: -200, background: 'radial-gradient(circle, rgba(124,111,247,0.14) 0%, transparent 70%)', animation: 'orbFloat1 22s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', width: 600, height: 600, borderRadius: '50%', bottom: -200, right: -150, background: 'radial-gradient(circle, rgba(249,115,22,0.1) 0%, transparent 70%)', animation: 'orbFloat2 28s ease-in-out infinite' }} />
      </div>
      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '24rem' }}>
        {/* Logo */}
        <div className="flex flex-col items-center mb-8 gap-3">
          <LogoLockup size="lg" />
          <p className="text-gray-400 text-sm">Your first pipeline is free. No credit card needed.</p>
        </div>

        {/* Card */}
        <div className="card">
          {/* Plan note from pricing CTA */}
          {planParam && (
            <div className="mb-4 text-center text-sm" style={{ color: '#f97316', background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: 8, padding: '8px 12px' }}>
              You selected the <strong>{planParam.charAt(0).toUpperCase() + planParam.slice(1)}</strong> plan — create your account to continue.
            </div>
          )}

          {/* Tabs */}
          <div className="flex mb-6 rounded-lg p-1" style={{ background: 'rgba(5,5,16,0.8)' }}>
            {(['login', 'register'] as const).map((t) => (
              <button
                key={t}
                onClick={() => { setTab(t); setErrors({}); setServerError(''); }}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                  tab === t ? 'bg-brand-500 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                {t === 'login' ? 'Sign In' : 'Sign Up'}
              </button>
            ))}
          </div>

          {/* Server error */}
          {serverError && (
            <div className="mb-4 bg-red-900/30 border border-red-700 rounded-lg px-3 py-2 text-red-400 text-sm">
              {serverError}
              {serverError.includes('already exists') && (
                <button type="button" onClick={() => { setTab('login'); setErrors({}); setServerError(''); }}
                  className="block mt-1 text-brand-400 hover:text-brand-300 text-xs font-medium">
                  Switch to Sign In →
                </button>
              )}
            </div>
          )}

          {/* Google Sign-In — rendered when VITE_GOOGLE_CLIENT_ID is set */}
          {GOOGLE_CLIENT_ID && (
            <>
              <div ref={googleBtnRef} className="w-full mb-4" style={{ minHeight: 44 }} />
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-px bg-gray-700" />
                <span className="text-xs text-gray-500">or</span>
                <div className="flex-1 h-px bg-gray-700" />
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {tab === 'register' && (
              <div>
                <label className="label">Full Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  onBlur={() => {
                    if (!form.name.trim()) setErrors({ ...errors, name: 'Name is required' });
                    else if (form.name.trim().length < 3) setErrors({ ...errors, name: 'Name must be at least 3 characters' });
                    else setErrors((prev) => { const { name: _, ...rest } = prev; return rest; });
                  }}
                  placeholder="Alex Founder"
                  className={`input-field ${errors.name ? 'error' : form.name.length >= 3 ? 'valid' : ''}`}
                  autoComplete="name"
                />
                {errors.name && <p className="error-text">{errors.name}</p>}
              </div>
            )}

            <div>
              <label className="label">Email address</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                onBlur={() => {
                  if (!form.email) setErrors({ ...errors, email: 'Email is required' });
                  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) setErrors({ ...errors, email: 'Invalid email' });
                  else setErrors((prev) => { const { email: _, ...rest } = prev; return rest; });
                }}
                placeholder="you@example.com"
                className={`input-field ${errors.email ? 'error' : ''}`}
                autoComplete="email"
              />
              {errors.email && <p className="error-text">{errors.email}</p>}
            </div>

            <div>
              <label className="label">Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  onBlur={() => {
                    if (!form.password) setErrors({ ...errors, password: 'Password is required' });
                    else if (form.password.length < 8) setErrors({ ...errors, password: 'At least 8 characters required' });
                    else setErrors((prev) => { const { password: _, ...rest } = prev; return rest; });
                  }}
                  placeholder={tab === 'register' ? 'Minimum 8 characters' : ''}
                  className={`input-field ${errors.password ? 'error' : ''}`}
                  autoComplete={tab === 'login' ? 'current-password' : 'new-password'}
                  style={{ paddingRight: 40 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                    color: 'rgba(255,255,255,0.35)', fontSize: 16, lineHeight: 1,
                  }}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && <p className="error-text">{errors.password}</p>}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary w-full mt-2 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {tab === 'login' ? 'Signing in...' : 'Creating account...'}
                </>
              ) : (
                tab === 'login' ? 'Sign In' : 'Create Account'
              )}
            </button>
            {tab === 'register' && (
              <p className="text-xs text-gray-400 mt-3 text-center leading-relaxed">
                By creating an account, you agree to our{' '}
                <a href="/terms" className="text-brand-400 hover:text-brand-300 transition-colors">Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" className="text-brand-400 hover:text-brand-300 transition-colors">Privacy Policy</a>.
              </p>
            )}
          </form>

          {/* Forgot password + sign-up nudge */}
          {tab === 'login' && (
            <div className="mt-4 pt-4 text-center" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
              <p className="text-xs text-gray-500 mb-2">
                <button type="button" onClick={() => navigate('/forgot-password')} className="text-brand-400 hover:text-brand-300 transition-colors font-medium">
                  Forgot your password?
                </button>
              </p>
              <p className="text-xs text-gray-500">
                New here?{' '}
                <button type="button" onClick={() => { setTab('register'); setErrors({}); setServerError(''); }} className="text-brand-400 hover:text-brand-300 transition-colors font-medium">
                  Create a free account
                </button>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
